import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines } from "./MaskedText";
import Field from "./Field";
import { api } from "@/lib/api";

const SIDE_IMG =
  "https://images.pexels.com/photos/33104714/pexels-photo-33104714.jpeg?auto=compress&cs=tinysrgb&w=900";

export default function Slide3Business({
  onNext,
  onBack,
  value,
  onChange,
  onEmailSaved,
  resumeToken,
}) {
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/categories")
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const set = (k) => (v) => onChange({ ...value, [k]: v });

  const selectedCategories = Array.isArray(value.product_categories)
    ? value.product_categories
    : value.product_category
    ? [value.product_category]
    : [];

  const toggleCategory = (name) => {
    const has = selectedCategories.includes(name);
    const next = has
      ? selectedCategories.filter((c) => c !== name)
      : [...selectedCategories, name];
    onChange({ ...value, product_categories: next, product_category: next[0] || "" });
  };

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value.email || "").trim());
  const validMobile = /^[0-9+\-\s]{7,15}$/.test((value.mobile || "").trim());

  const handleContinue = async () => {
    const missing = [];
    if (!value.brand_name) missing.push("Brand name");
    if (!value.owner_name) missing.push("Owner name");
    if (!validMobile) missing.push("Valid mobile number");
    if (!validEmail) missing.push("Valid email");
    if (selectedCategories.length === 0)
      missing.push("At least one product category");
    if (missing.length) {
      toast.error(`Please fill: ${missing.join(", ")}`);
      return;
    }

    // Silently save progress once we have email (idempotent per email)
    if (!resumeToken) {
      setSaving(true);
      try {
        const { data } = await api.post("/onboarding/save-progress", {
          email: value.email.trim(),
          owner_name: value.owner_name,
        });
        onEmailSaved?.(value.email.trim(), data.resume_token);
        toast.success("We've emailed you a resume link — just in case.");
      } catch {
        /* non-blocking */
      } finally {
        setSaving(false);
      }
    }
    onNext();
  };

  return (
    <div
      className="w-full grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-10 lg:gap-16 items-start"
      data-testid="slide-3-business"
    >
      {/* LEFT — form */}
      <div className="flex flex-col">
        <div className="eyebrow">Chapter 03 · Your Brand, In Your Words</div>
        <h2 className="mt-3 font-editorial text-[36px] sm:text-[44px] md:text-[54px] leading-[1.05] tracking-[-0.01em]">
          <MaskedLines
            lines={[
              "Tell us who",
              <span key="you" className="italic text-[color:var(--sbh-terracotta)]">you are.</span>,
            ]}
          />
        </h2>
        <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-[color:var(--sbh-ink-2)]">
          A few essentials so we can prepare your storefront and get in touch. Nothing here is public — this is between you and our onboarding team.
        </p>

        <motion.div
          className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <Field
            label="Brand / Business Name"
            required
            value={value.brand_name}
            onChange={set("brand_name")}
            placeholder="Nolen Gur Co."
            testId="business-brand-name"
          />
          <Field
            label="Owner's Full Name"
            required
            value={value.owner_name}
            onChange={set("owner_name")}
            placeholder="Ananya Sen"
            testId="business-owner-name"
          />
          <Field
            label="Mobile Number (for calls)"
            required
            type="tel"
            value={value.mobile}
            onChange={set("mobile")}
            placeholder="+91 98…"
            testId="business-mobile"
            autoComplete="tel"
          />
          <Field
            label="WhatsApp Number (if different)"
            type="tel"
            value={value.whatsapp}
            onChange={set("whatsapp")}
            placeholder="+91 98…"
            testId="business-whatsapp"
          />
          <div className="md:col-span-2">
            <Field
              label="Email Address"
              required
              type="email"
              value={value.email}
              onChange={set("email")}
              placeholder="you@brand.com"
              testId="business-email"
              autoComplete="email"
            />
          </div>
          <div className="md:col-span-2 mt-6">
            <div className="field filled block w-full" style={{ borderBottom: "none", paddingTop: 0 }}>
              <span className="eyebrow text-[color:var(--sbh-terracotta)]">
                Product Categories
                <span className="text-[color:var(--sbh-terracotta)]"> *</span>
                <span className="ml-2 text-[color:var(--sbh-muted)] tracking-[0.16em]">
                  · select one or more
                </span>
              </span>
            </div>
            <div
              className="mt-4 flex flex-wrap gap-2 md:gap-3"
              data-testid="category-picker"
              role="group"
              aria-label="Product Categories"
            >
              {categories.map((name) => {
                const selected = selectedCategories.includes(name);
                return (
                  <button
                    type="button"
                    key={name}
                    onClick={() => toggleCategory(name)}
                    aria-pressed={selected}
                    data-testid={`category-chip-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    className={
                      "text-[12.5px] tracking-[0.02em] font-body px-3.5 py-2 border transition-colors " +
                      (selected
                        ? "bg-[color:var(--sbh-terracotta)] text-[color:var(--sbh-cream)] border-[color:var(--sbh-terracotta)]"
                        : "bg-transparent text-[color:var(--sbh-ink-2)] border-[color:var(--sbh-border)] hover:border-[color:var(--sbh-terracotta)] hover:text-[color:var(--sbh-terracotta)]")
                    }
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            {selectedCategories.length > 0 && (
              <div className="mt-3 text-[12px] tracking-[0.02em] text-[color:var(--sbh-muted)]">
                {selectedCategories.length} selected
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <Field
              label="Tell Us About Your Brand"
              type="textarea"
              value={value.brand_story}
              onChange={set("brand_story")}
              placeholder="e.g., We've been making traditional nolen gur sweets for 3 generations in Bardhaman…"
              testId="business-story"
              maxLength={800}
            />
          </div>
        </motion.div>

        <div className="mt-14 flex items-center justify-between gap-6">
          <button className="btn-ghost" onClick={onBack} data-testid="business-back-btn">
            <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back
          </button>
          <button
            className="btn-editorial"
            onClick={handleContinue}
            disabled={saving}
            data-testid="business-continue-btn"
          >
            {saving ? "Saving…" : "Continue to Address & Documents"}
            <ArrowRight className="arrow w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* RIGHT — editorial visual */}
      <motion.aside
        className="hidden lg:block relative w-full h-[620px]"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="absolute inset-0 border border-[color:var(--sbh-border)]" />
        <div className="absolute inset-6 overflow-hidden">
          <img
            src={SIDE_IMG}
            alt="Warm Bengali kitchen — chai in clay cups"
            className="w-full h-full object-cover"
            draggable="false"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--sbh-deep-red)]/30 to-transparent" />
          <div className="grain" />
        </div>
        <div className="absolute bottom-6 left-6 right-6 bg-[color:var(--sbh-cream)] border-t border-[color:var(--sbh-border)] p-5">
          <div className="eyebrow">Fig. 02</div>
          <p className="mt-2 font-editorial italic text-[18px] leading-snug text-[color:var(--sbh-ink)]">
            &ldquo;The best commerce is the one that still smells like home.&rdquo;
          </p>
        </div>
      </motion.aside>
    </div>
  );
}
